/**
 * ModalContext — centralized modal routing for the simple-mode editor.
 *
 * Eliminates prop drilling of `activeSimpleModal` / `setActiveSimpleModal`
 * through App → SimpleModeEditor → SimpleActionRail / MobileBottomBar.
 *
 * Usage:
 *   // In App.tsx (provider)
 *   <ModalProvider value={{ activeModal, setActiveModal }}>
 *     ...children...
 *   </ModalProvider>
 *
 *   // In any descendant
 *   const { activeModal, setActiveModal } = useModal();
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { SimpleModalType } from '../components/SimpleMode/SimpleActionRail';

interface ModalContextValue {
  activeModal: SimpleModalType;
  setActiveModal: (modal: SimpleModalType) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: ModalContextValue;
}) {
  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}

/**
 * Hook to access the active modal state.
 * Must be used inside a <ModalProvider>.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error('useModal must be used within a <ModalProvider>');
  }
  return ctx;
}
